import React, { useEffect, useState } from 'react';
import { Drawer, List, ListItem, ListItemIcon, ListItemText, IconButton, Box, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import {
    Home as HomeIcon,
    CalendarToday as CalendarIcon,
    Group as GroupIcon,
    Sports as SportsIcon,
    LocationOn as LocationOnIcon,
    BarChart as BarChartIcon,
    Event as EventIcon,
    VideoLibrary as VideoLibraryIcon,
    PlaylistAddCheck as WaitlistIcon,
    ChevronLeft as ChevronLeftIcon,
    ChevronRight as ChevronRightIcon,
    ExitToApp as ExitToAppIcon,
    Store as StoreIcon,
    SupervisorAccount as SupervisorAccountIcon,
    Business as BusinessIcon,
    PeopleAlt as PeopleAltIcon,
    PersonAdd as ApplicantsIcon,
    Work as WorkIcon,
    TrendingUp as TrendingUpIcon,
    Outbox as OutboxIcon,
    AutoAwesome as AutoAwesomeIcon,
    Inventory2 as Inventory2Icon,
    Science as ScienceIcon,
    Campaign as CampaignIcon,
    DescriptionOutlined as TranscriptIcon,
    AccountTree as AccountTreeIcon,
} from '@mui/icons-material';

import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';


import BusinessCenterIcon from '@mui/icons-material/BusinessCenter'; // ✅ NEW ICON
import MoveToInboxIcon from '@mui/icons-material/MoveToInbox';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PriceChangeIcon from '@mui/icons-material/PriceChange';
import { useEmployee } from '../../contexts/EmployeeContext';
import { usePricingAccess } from '../../hooks/usePricingAccess';

const drawerWidth = 240;

const CustomDrawer = styled(Drawer)(({ open }) => ({
    width: open ? drawerWidth : 60,
    transition: 'width 0.3s',
    whiteSpace: 'nowrap',
    flexShrink: 0,
    '& .MuiDrawer-paper': {
        width: open ? drawerWidth : 60,
        transition: 'width 0.3s',
        overflowX: 'hidden',
        backgroundColor: '#202020',
        color: '#fff',
    },
}));

const CustomListItemIcon = styled(ListItemIcon)({
    color: '#12c099',
});

// Section header label. Mint for Enterprise AI, muted gray for Workspace.
const sectionLabelSx = (mint) => ({
    fontSize: '10px',
    fontWeight: 700,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    padding: '12px 16px',
    color: mint ? '#12c099' : 'rgba(255,255,255,0.35)',
});

// "NEW" badge that sits to the right of an item label.
const badgeSx = {
    backgroundColor: '#12c099',
    color: '#1c1c1c',
    fontSize: '9px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    padding: '2px 6px',
    borderRadius: '3px',
    lineHeight: 1,
};

const Sidebar = ({ open, handleDrawerToggle, handleLogout }) => {
    const [totalLeads, setTotalLeads] = useState(0);
    const [totalPreferences, setTotalPreferences] = useState(0);
    const { employee } = useEmployee();
    // T6-PRICING-PANEL-01: the Pricing tab shows only once mgmt-backend's gate lets this staff member through.
    const pricingAccess = usePricingAccess();
    const location = useLocation();

    const isCityManager = [
        'City Manager I',
        'City Manager II',
        'City Manager III'
    ].includes(employee?.title);

    useEffect(() => {
        const fetchTotalPreferences = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/preferences/count`);
                setTotalPreferences(response.data.count);
            } catch (error) {
                console.error("❌ Error fetching preferences:", error);
            }
        };
        fetchTotalPreferences();
    }, []);

    useEffect(() => {
        const fetchTotalLeads = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/leads`);
                setTotalLeads(response.data.length);
            } catch (error) {
                console.error("❌ Error fetching leads:", error);
            }
        };
        fetchTotalLeads();
    }, []);

    const ENTERPRISE_AI_ITEMS = [
        { to: '/genesis', label: 'Genesis', icon: <AutoAwesomeIcon />, badge: 'NEW' },
        { to: '/agent-builder', label: 'Agent Builder', icon: <AccountTreeIcon />, badge: 'NEW' },
        { to: '/transcripts', label: 'Transcripts', icon: <TranscriptIcon />, badge: 'NEW' },
        { to: '/outbound', label: 'GTM', icon: <CampaignIcon />, badge: 'NEW' },
        { to: '/inbound-leads', label: 'Leads', icon: <MoveToInboxIcon />, badge: 'NEW' },
        { to: '/memory', label: 'Memory', icon: <Inventory2Icon /> },
        { to: '/eval', label: 'Eval Harness', icon: <ScienceIcon /> },
        { to: '/enterprises', label: 'Enterprise', icon: <BusinessCenterIcon /> },
        { to: '/money', label: 'Money', icon: <AccountBalanceWalletIcon /> },
        { to: '/united-pricing', label: 'Pricing', icon: <PriceChangeIcon />, badge: 'NEW', requiresPricingRole: true },
    ];

    // Workspace items in current order, MINUS the existing Enterprise item which
    // now lives in the Enterprise AI section above.
    const WORKSPACE_ITEMS = [
        { to: '/whook', label: 'Whook', icon: '🧠' },
        { to: '/dashboard', label: 'Overview', icon: <HomeIcon />, hideIfCityManager: true },
        { to: '/BookingDashboard', label: 'Dashboard', icon: <CalendarIcon /> },
        { to: '/leads', label: 'Leads', icon: <PeopleAltIcon />, countKey: 'leads' },
        { to: '/preferences', label: 'Preferences', icon: <TrendingUpIcon />, countKey: 'preferences' },
        { to: '/outreach', label: 'Outreach', icon: <OutboxIcon /> },
        { to: '/waitlist', label: 'Waitlist', icon: <WaitlistIcon /> },
        { to: '/athletes', label: 'Users', icon: <GroupIcon /> },
        { to: '/coaches', label: 'Instructors', icon: <SportsIcon /> },
        { to: '/sitemanagers', label: 'Site staff', icon: <SupervisorAccountIcon /> },
        { to: '/couriers', label: 'Couriers', icon: <LocalShippingIcon /> },
        { to: '/employees', label: 'Employees', icon: <WorkIcon /> },
        { to: '/applicants', label: 'Applicants', icon: <ApplicantsIcon /> },
        { to: '/facilities/countries', label: 'Facilities', icon: <BusinessIcon /> },
        { to: '/markets', label: 'Markets', icon: <LocationOnIcon /> },
        { to: '/pricing', label: 'Pricing', icon: <AttachMoneyIcon /> },
        { to: '/sessions', label: 'Sessions', icon: <EventIcon /> },
        { to: '/financials', label: 'Financials', icon: <BarChartIcon /> },
        { to: '/contentfeed', label: 'Content Feed', icon: <VideoLibraryIcon /> },
        { to: '/marketing', label: 'Marketing', icon: <StoreIcon /> },
    ];

    const renderNavItem = (item, isEnterpriseAI) => {
        if (item.hideIfCityManager && isCityManager) return null;
        if (item.requiresPricingRole && pricingAccess !== 'allowed') return null;

        const isActive =
            location.pathname === item.to ||
            location.pathname.startsWith(item.to + '/');

        let primaryNode = item.label;
        if (item.countKey === 'leads') {
            primaryNode = (
                <Typography variant="body1">
                    Leads <strong>({totalLeads > 0 ? totalLeads + 200 : 0})</strong>
                </Typography>
            );
        } else if (item.countKey === 'preferences') {
            primaryNode = (
                <Typography variant="body1">
                    Preferences <strong>({totalPreferences.toLocaleString()})</strong>
                </Typography>
            );
        }

        return (
            <ListItem
                key={item.to}
                button
                component={Link}
                to={item.to}
                sx={isEnterpriseAI ? { position: 'relative' } : undefined}
            >
                {isEnterpriseAI && isActive && (
                    <Box
                        sx={{
                            position: 'absolute',
                            left: 0,
                            top: 8,
                            bottom: 8,
                            width: 3,
                            bgcolor: '#12c099',
                            borderTopRightRadius: 2,
                            borderBottomRightRadius: 2,
                        }}
                    />
                )}
                <CustomListItemIcon>{item.icon}</CustomListItemIcon>
                {open && (
                    <ListItemText
                        primary={
                            item.badge ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Box component="span">{primaryNode}</Box>
                                    <Box component="span" sx={badgeSx}>{item.badge}</Box>
                                </Box>
                            ) : (
                                primaryNode
                            )
                        }
                    />
                )}
            </ListItem>
        );
    };

    return (
        <CustomDrawer variant="permanent" open={open}>
            <IconButton onClick={handleDrawerToggle} sx={{ color: '#12c099' }}>
                {open ? <ChevronLeftIcon /> : <ChevronRightIcon />}
            </IconButton>

            {/* Logo */}
            <Box sx={{ display: 'flex', justifyContent: 'center', padding: '3px 0' }}>
                {open && (
                    <img
                        src="https://d3dbfxrvl5y81z.cloudfront.net/unitedai.png"
                        alt="United AI"
                        style={{ height: '30px' }}
                    />
                )}
            </Box>

            <List sx={{ flexGrow: 1 }}>

                {/* ENTERPRISE AI SECTION */}
                {open && <Typography sx={sectionLabelSx(true)}>Enterprise AI</Typography>}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {ENTERPRISE_AI_ITEMS.map((item) => renderNavItem(item, true))}
                </Box>

                {/* DIVIDER */}
                <Box sx={{ borderBottom: '1px solid rgba(255,255,255,0.06)', mx: 2, my: 1.5 }} />

                {/* WORKSPACE SECTION */}
                {open && <Typography sx={sectionLabelSx(false)}>Workspace</Typography>}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', mb: 2 }}>
                    {WORKSPACE_ITEMS.map((item) => renderNavItem(item, false))}
                </Box>

            </List>

            {/* Logout */}
            <List>
                <ListItem button onClick={handleLogout}>
                    <CustomListItemIcon><ExitToAppIcon /></CustomListItemIcon>
                    {open && <ListItemText primary="Logout" />}
                </ListItem>
            </List>
        </CustomDrawer>
    );
};

export default Sidebar;
